import React, { useMemo, useState } from 'react';
import { TouchableOpacity, useWindowDimensions, View } from 'react-native'

import api from "@/utils/api"
import tw from '@/styles/tailwind';
import Text from '@/components/Text';
import { useQuery } from '@tanstack/react-query';

import dayjs from 'dayjs'

import FileCell from './components/FileCell'


// Card blanca con titulo a la izquierda y accion opcional a la derecha.
function Card({ title, right, children, style }: any) {
  return (
    <View style={tw.style(`bg-white rounded-xl border border-gray-100 px-5 py-4`, style)}>
      {(title || right) && (
        <View style={tw`row justify-between items-center mb-3`}>
          {
            title
              ? (typeof title === 'string'
                  ? <Text style={tw`text-gray-800 font-600 text-base`}>{title}</Text>
                  : title)
              : <View />
          }
          {right}
        </View>
      )}
      {children}
    </View>
  )
}


function AttributeRow({ label, value, last }: any) {
  return (
    <View style={tw.style(`row justify-between py-2.5`, !last && `border-b border-gray-100`)}>
      <Text style={tw`text-gray-500`}>{label}</Text>
      <Text style={tw`text-gray-800 font-500 text-right flex-shrink ml-3`}>{value}</Text>
    </View>
  )
}


// entity_type -> etiqueta legible ("Persona Moral" / "Persona Física").
function entityTypeLabel(v: any) {
  if (!v) return null
  const s = String(v).toLowerCase()
  if (s.includes('moral')) return 'Persona Moral'
  if (s.includes('fis')) return 'Persona Física'
  return v
}


// PLACEHOLDER: el backend no tiene correo para facturas; generamos uno visual a partir del nombre.
function invoiceEmailPlaceholder(name: any) {
  // normalize('NFD') separa los acentos y el [^a-z0-9] los elimina junto con espacios/simbolos.
  const slug = String(name || 'proveedor').toLowerCase().normalize('NFD').replace(/[^a-z0-9]/g, '')
  return `${slug}-c029an2c@facturas.vinte.mx`
}


// PLACEHOLDER: bitacora de acciones (no existe endpoint de actividad del proveedor todavia).
const MOCK_AUDIT_LOG = [
  { type: 'upload', action: 'Documento subido',    target: 'Cotización/presupuesto (actualizada)',     user: 'Administrador', date: '28/4/2025' },
  { type: 'delete', action: 'Documento eliminado', target: 'Cotización/presupuesto (versión anterior)', user: 'Administrador', date: '25/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Carátula bancaria',        user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'REPSE',                     user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Cotización/presupuesto',    user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Comprobante de domicilio',  user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Opinión SAT',               user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Declaración anual',         user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'INE del representante',     user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'RFC',                       user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Acta constitutiva',         user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Cartera de clientes',       user: 'Administrador', date: '22/4/2025' },
  { type: 'upload', action: 'Documento subido',    target: 'Currículum',                user: 'Administrador', date: '22/4/2025' },
]


function AuditLogRow({ entry, last }: any) {
  const isDelete = entry.type === 'delete'

  return (
    <View style={tw.style(`row items-center py-3`, !last && `border-b border-gray-100`)}>
      <View style={tw.style(`h-9 w-9 rounded-full items-center justify-center mr-3`, isDelete ? `bg-red-50` : `bg-green-50`)}>
        <Text style={tw.style(isDelete ? `text-red-500` : `text-green-600`)}>{isDelete ? '🗑️' : '⬆️'}</Text>
      </View>

      <View style={tw`flex-1`}>
        <Text style={tw`text-gray-800`}>
          <Text style={tw`font-600`}>{entry.action}</Text>
          <Text style={tw`text-gray-400`}> — {entry.target}</Text>
        </Text>
        <Text style={tw`text-gray-400 text-sm mt-0.5`}>{entry.user}</Text>
      </View>

      <Text style={tw`text-gray-400 text-sm ml-3`}>{entry.date}</Text>
    </View>
  )
}


export default function ProviderDetailScreen({ route }: any) {
  const { providerId } = route.params

  const { isInitialLoading, error, data: provider, refetch } = useQuery({
    queryKey: ['vinte.providers.detail', providerId],
    queryFn: () => api.get(`/vinte/providers/${providerId}`).then(response => response.data),
  })

  const { width } = useWindowDimensions()
  const twoCol = width > 1100

  // PLACEHOLDER: enlace de carga (sin backend). Al generar, simulamos una URL con uuid.
  const [uploadLink, setUploadLink] = useState<string | null>(null)

  const generateUploadLink = () => {
    const uuid = (globalThis as any)?.crypto?.randomUUID?.() ?? 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
    setUploadLink(`https://vinte.ding-dong.mx/${uuid}`)
  }

  const copyUploadLink = () => {
    if (uploadLink) (globalThis as any)?.navigator?.clipboard?.writeText?.(uploadLink)
  }

  const deactivateUploadLink = () => setUploadLink(null)

  // !!
  const bankAccount = provider?.bank_accounts?.[0]

  const documents = [
    { name: 'Curriculum', key: 'curriculum', documentFile: null, uploadedAt: null },
    { name: 'Cartera de clientes', key: 'cartera_de_clientes', documentFile: null, uploadedAt: null },
    { name: 'Acta constitutiva', key: 'acta_constitutiva', documentFile: null, uploadedAt: null },
    { name: 'Constancia situación fiscal', key: 'constancia_situacion_fiscal', documentFile: null, uploadedAt: null },
    { name: 'Poder Notarial del RL', key: 'poder_notarial_del_rl', documentFile: null, uploadedAt: null },
    { name: 'INE del Representante', key: 'ine_del_representante', documentFile: null, uploadedAt: null },
    { name: 'Declaración anual', key: 'declaracion_anual', documentFile: null, uploadedAt: null },
    { name: 'Opinión SAT', key: 'opinion_sat', documentFile: null, uploadedAt: null },
    { name: 'Comprobante de domicilio', key: 'comprobante_de_domicilio', documentFile: null, uploadedAt: null },
    { name: 'Cotización / presupuesto', key: 'cotizacion_presupuesto', documentFile: null, uploadedAt: null },
    { name: 'REPSE', key: 'repse', documentFile: null, uploadedAt: null },
    { name: 'Registro patronal IMSS', key: 'registro_patronal_imss', documentFile: null, uploadedAt: null },
    { name: 'Carátula bancaria', key: 'caratula_bancaria', documentFile: null, uploadedAt: null },
  ]


  const employeeDocuments = useMemo(() => {
    if (!provider?.documents) return documents

    const docsWithFile = documents.map(document => {
      document.documentFile = provider.documents.find((e: any) => e.document_tag == document.key)
      return document
    })

    return docsWithFile
  }, [provider?.documents])


  // Progreso de documentos cargados (subidos / total).
  const uploadedDocsCount = employeeDocuments.filter(d => d.documentFile).length
  const totalDocsCount = employeeDocuments.length
  const docsPct = totalDocsCount ? (uploadedDocsCount / totalDocsCount) : 0


  if (isInitialLoading)
    return (<Text style={tw`my-8 text-lg text-center text-gray-400`}>Cargando...</Text>)


  return (
    <View style={tw`p-6 gap-y-5`}>

      {/* Datos generales y de banco */}
      <View style={tw.style(twoCol ? `flex-row gap-x-5` : `flex-col gap-y-5`)}>
        {/* Card izquierda: datos generales */}
        <View style={tw.style(`bg-white border border-gray-100 rounded-xl px-5 py-4`, twoCol ? `flex-1` : `w-full`)}>
          <View style={tw`row justify-between items-start`}>
            <View style={tw`flex-1 pr-3`}>
              <Text style={tw`text-gray-900 font-700 text-base`}>{provider?.name}</Text>
              {
                entityTypeLabel(provider?.entity_type) &&
                  <Text style={tw`text-gray-500 mt-0.5`}>{entityTypeLabel(provider?.entity_type)}</Text>
              }
              {/* RFC: el diseño no lo muestra, pero no lo quitamos */}
              {
                provider?.rfc &&
                  <Text style={tw`text-gray-400 text-sm mt-0.5`}>RFC: {provider.rfc}</Text>
              }
            </View>

            {/* PLACEHOLDER: boton Editar (sin accion por ahora) */}
            <TouchableOpacity style={tw`row items-center border border-gray-200 rounded-lg px-3 py-1.5`}>
              <Text style={tw`text-gray-600 text-sm`}>✎ Editar</Text>
            </TouchableOpacity>
          </View>

          <View style={tw`border-t border-gray-100 mt-3 mb-1`} />

          <AttributeRow label={"Categoría"} value={provider?.type} last />

          {/* PLACEHOLDER: correo para facturas (no existe en backend) */}
          <View style={tw`mt-3`}>
            <Text style={tw`text-gray-500 mb-1.5`}>Correo para facturas</Text>
            <View style={tw`row items-center bg-secondary/5 border border-secondary/20 rounded-lg px-3 py-2.5`}>
              <Text style={tw`mr-2`}>✉️</Text>
              <Text style={tw`text-secondary flex-1`}>{invoiceEmailPlaceholder(provider?.name)}</Text>
            </View>
          </View>
        </View>

        {/* Card derecha: datos bancarios */}
        <View style={tw.style(`bg-white border border-gray-100 rounded-xl px-5 py-4`, twoCol ? `flex-1` : `w-full`)}>
          <View style={tw`row justify-between items-center mb-3`}>
            <Text style={tw`text-gray-800 font-600 text-base`}>Datos Bancarios</Text>
            <Text style={tw`text-gray-500`}>{bankAccount?.bank}</Text>
          </View>

          <AttributeRow label={"No. de cuenta"} value={bankAccount?.account_number} />
          <AttributeRow label={"Clabe"} value={bankAccount?.clabe} last />
        </View>
      </View>


      {/* PLACEHOLDER: enlace de carga para proveedor (feature nueva, sin backend) */}
      <View style={tw`row items-center justify-between bg-white border border-gray-100 rounded-xl px-5 py-4 mb-4`}>
        <View style={tw`row items-center flex-1 pr-3`}>
          <View style={tw`h-9 w-9 rounded-full bg-secondary/10 items-center justify-center mr-3`}>
            <Text>🔗</Text>
          </View>
          <View style={tw`flex-1`}>
            <Text style={tw`text-gray-800 font-600`}>Enlace de carga para proveedor</Text>
            <Text style={tw`text-gray-400 text-sm`}>El proveedor puede subir sus documentos en este enlace</Text>

            {
              uploadLink &&
                <View style={tw`row items-center bg-secondary/5 border border-secondary/20 rounded-lg px-3 py-2 mt-2`}>
                  <Text style={tw`text-secondary flex-1`} selectable>{uploadLink}</Text>
                  <TouchableOpacity onPress={copyUploadLink} style={tw`ml-2 px-2 py-1`}>
                    <Text style={tw`text-secondary text-sm font-500`}>Copiar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={deactivateUploadLink} style={tw`ml-1 px-2 py-1`}>
                    <Text style={tw`text-red-500 text-sm font-500`}>Desactivar</Text>
                  </TouchableOpacity>
                </View>
            }
          </View>
        </View>

        {/* PLACEHOLDER: genera y muestra una URL simulada con uuid */}
        <TouchableOpacity style={tw`bg-secondary rounded-lg px-4 py-2.5`} onPress={generateUploadLink}>
          <Text style={tw`text-white font-500`}>{uploadLink ? 'Regenerar' : 'Generar enlace'}</Text>
        </TouchableOpacity>
      </View>


      {/* Documentos del proveedor */}
      <View style={tw``}>
        <View style={tw`row items-center justify-between mb-4`}>
          <View style={tw`row items-center`}>
            <View style={tw`h-8 w-8 rounded-full bg-secondary/10 items-center justify-center mr-2`}>
              <Text>👤</Text>
            </View>
            <Text style={tw`text-gray-800 font-600 text-base`}>Documentos del proveedor</Text>
          </View>

          {/* Barra de progreso de documentos cargados */}
          <View style={tw`row items-center`}>
            <View style={tw`h-1.5 w-[180px] bg-gray-300 rounded-full overflow-hidden mr-2`}>
              <View style={tw.style(`h-full bg-secondary rounded-full`, { width: `${docsPct * 100}%` })} />
            </View>
            <Text style={tw`text-gray-400 text-sm`}>{uploadedDocsCount}/{totalDocsCount} docs</Text>
          </View>
        </View>

        <View style={tw`row items-start flex-wrap gap-x-4`}>
          {
            employeeDocuments.map(document => {
              const documentFile = document.documentFile as any
              const uploadedAtStr = documentFile?.created_at ? dayjs(documentFile?.created_at).format('DD/MM/YYYY') : null

              return (
                <FileCell
                  key={document.key}
                  title={document.name}
                  extraData={{ document_tag: document.key }}
                  uploadUrl={`/vinte/providers/${provider.uuid}/documents`}
                  deleteUrl={`/vinte/providers/${provider.uuid}/documents/${documentFile?.uuid}`}
                  defaultImage={documentFile?.file_url}
                  uploadedAt={uploadedAtStr}
                  onFileDelete={() => refetch()}
                  onFileCreated={() => refetch()}
                />
              )
            })
          }
        </View>
      </View>


      {/* PLACEHOLDER: bitacora de acciones (datos mock) */}
      <Card
        title={
          <View style={tw`row items-center`}>
            <View style={tw`h-8 w-8 rounded-full bg-secondary/10 items-center justify-center mr-2`}>
              <Text>📝</Text>
            </View>
            <Text style={tw`text-gray-800 font-600 text-base`}>Bitácora de acciones</Text>
          </View>
        }
        right={<Text style={tw`text-gray-400 text-sm`}>{MOCK_AUDIT_LOG.length} registros</Text>}
      >
        {
          MOCK_AUDIT_LOG.map((entry, i) => (
            <AuditLogRow key={i} entry={entry} last={i === MOCK_AUDIT_LOG.length - 1} />
          ))
        }
      </Card>

    </View>
  )
}
